import type {
  Cin7AdditionalAttributes,
  Cin7SaleAddress,
  Cin7SaleAdditionalCharge,
  Cin7SaleLine,
  Cin7SaleOrder,
  Cin7SaleOrderResult,
  NewCin7SaleOrder,
  NewCin7SaleOrderLine,
} from "@/api/dtos";
import type {
  SaleAddress,
  SaleAdditionalCharge,
  SaleOrder,
  SaleOrderInput,
  SaleOrderLine,
} from "@/api/sale/_models/Sale";

export const toSaleAddressFromCin7SaleAddress = (
  a: Cin7SaleAddress,
): SaleAddress => {
  return {
    line1: a.Line1,
    line2: a.Line2,
    city: a.City,
    state: a.State,
    postcode: a.Postcode,
    country: a.Country,
  };
};

export const toCin7SaleAddressFromSaleAddress = (
  a: SaleAddress,
): Cin7SaleAddress => {
  return {
    Line1: a.line1,
    Line2: a.line2,
    City: a.city,
    State: a.state,
    Postcode: a.postcode,
    Country: a.country,
  };
};

export const emptyCin7AdditionalAttributes = (): Cin7AdditionalAttributes => {
  return {
    AdditionalAttribute1: "",
    AdditionalAttribute2: "",
    AdditionalAttribute3: "",
    AdditionalAttribute4: "",
    AdditionalAttribute5: "",
    AdditionalAttribute6: "",
    AdditionalAttribute7: "",
    AdditionalAttribute8: "",
    AdditionalAttribute9: "",
    AdditionalAttribute10: "",
  };
};

export const toSaleOrderLineFromCin7SaleLine = (
  line: Cin7SaleLine,
): SaleOrderLine => {
  return {
    productId: line.ProductID,
    sku: line.SKU,
    name: line.Name,
    quantity: line.Quantity,
    price: line.Price,
    discount: line.Discount,
    tax: line.Tax,
    total: line.Total,
    taxRule: line.TaxRule,
    comment: line.Comment,
  };
};

export const toSaleAdditionalChargeFromCin7SaleAdditionalCharge = (
  charge: Cin7SaleAdditionalCharge,
): SaleAdditionalCharge => {
  return {
    description: charge.Description,
    price: charge.Price,
    quantity: charge.Quantity,
    discount: charge.Discount,
    tax: charge.Tax,
    total: charge.Total,
    taxRule: charge.TaxRule,
    comment: charge.Comment,
  };
};

export const toSaleOrderFromCin7SaleOrder = (
  order: Cin7SaleOrder,
): SaleOrder => {
  return {
    number: order.SaleOrderNumber,
    memo: order.Memo,
    status: order.Status,
    lines: order.Lines.map((x) => toSaleOrderLineFromCin7SaleLine(x)),
    additionalCharges: order.AdditionalCharges.map((x) =>
      toSaleAdditionalChargeFromCin7SaleAdditionalCharge(x),
    ),
    totalBeforeTax: order.TotalBeforeTax,
    tax: order.Tax,
    total: order.Total,
  };
};

/** The /sale/order resource is keyed by SaleID and has no SaleOrderNumber, so `number` is blank. */
export const toSaleOrderFromCin7SaleOrderResult = (
  result: Cin7SaleOrderResult,
): SaleOrder => {
  return {
    number: "",
    memo: result.Memo,
    status: result.Status,
    lines: result.Lines.map((x) => toSaleOrderLineFromCin7SaleLine(x)),
    additionalCharges: result.AdditionalCharges.map((x) =>
      toSaleAdditionalChargeFromCin7SaleAdditionalCharge(x),
    ),
    totalBeforeTax: result.TotalBeforeTax,
    tax: result.Tax,
    total: result.Total,
  };
};

/** Domain line → Cin7 line; Total is computed as Quantity * Price - Discount, since Cin7 rejects a mismatch. */
export const toCin7SaleOrderLineFromSaleOrderLine = (
  line: SaleOrderLine,
): NewCin7SaleOrderLine => {
  return {
    ProductID: line.productId,
    SKU: line.sku,
    Name: line.name,
    Quantity: line.quantity,
    Price: line.price,
    Discount: line.discount,
    Tax: line.tax,
    TaxRule: line.taxRule,
    Comment: line.comment,
    Total: line.quantity * line.price - line.discount,
  };
};

export const toCin7SaleAdditionalChargeFromSaleAdditionalCharge = (
  charge: SaleAdditionalCharge,
): Cin7SaleAdditionalCharge => {
  return {
    Description: charge.description,
    Price: charge.price,
    Quantity: charge.quantity,
    Discount: charge.discount,
    Tax: charge.tax,
    Total: charge.total,
    TaxRule: charge.taxRule,
    Comment: charge.comment,
  };
};

/** Attaches line items to an existing sale. status defaults to "AUTHORISED". */
export const toNewCin7SaleOrderFromSaleOrderInput = (
  input: SaleOrderInput,
): NewCin7SaleOrder => {
  return {
    SaleID: input.saleId,
    CombineAdditionalCharges: false,
    Memo: input.memo ?? "",
    Status: input.status ?? "DRAFT",
    Lines: input.lines.map((x) => toCin7SaleOrderLineFromSaleOrderLine(x)),
    AdditionalCharges: (input.additionalCharges ?? []).map((x) =>
      toCin7SaleAdditionalChargeFromSaleAdditionalCharge(x),
    ),
  };
};
